import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

const PHONE = '07760 881212';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const LOCATIONS_OG_IMAGE = 'https://public.readdy.ai/ai/img_res/687d82c85f6b920db18e8a55c233bd27.jpg';

const locationsJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/locations#webpage`,
    url: `${siteUrl}/locations`,
    name: 'Electrician Retford, Carlton in Lindrick & Shireoaks | Areas Covered',
    description:
      'Reliable electrician covering Retford, Carlton in Lindrick, and Shireoaks — fast response from our Worksop base. Emergency callouts, rewiring, EICR and more.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    about: { '@id': `${siteUrl}/#business` },
    inLanguage: 'en-GB',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Locations', item: `${siteUrl}/locations` },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Electrician Service Locations',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'LocalBusiness',
          '@id': `${siteUrl}/locations#retford`,
          name: 'Dukeries Electrical — Electrician Retford',
          description:
            'Dukeries Electrical — professional electrical services in Retford, Nottinghamshire — emergency callouts, rewiring, EICR and domestic work.',
          telephone: '07760 881212',
          areaServed: {
            '@type': 'City',
            name: 'Retford',
            addressRegion: 'Nottinghamshire',
            addressCountry: 'GB',
          },
          url: `${siteUrl}/locations`,
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'LocalBusiness',
          '@id': `${siteUrl}/locations#carlton`,
          name: 'Dukeries Electrical — Electrician Carlton in Lindrick',
          description:
            'Dukeries Electrical — local electrical services in Carlton in Lindrick, South Yorkshire — just minutes from our Worksop base.',
          telephone: '07760 881212',
          areaServed: {
            '@type': 'Place',
            name: 'Carlton in Lindrick',
            addressRegion: 'South Yorkshire',
            addressCountry: 'GB',
          },
          url: `${siteUrl}/locations`,
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'LocalBusiness',
          '@id': `${siteUrl}/locations#shireoaks`,
          name: 'Dukeries Electrical — Electrician Shireoaks',
          description:
            'Dukeries Electrical — fast-response electrical services in Shireoaks, Nottinghamshire — 2 miles from Worksop.',
          telephone: '07760 881212',
          areaServed: {
            '@type': 'Place',
            name: 'Shireoaks',
            addressRegion: 'Nottinghamshire',
            addressCountry: 'GB',
          },
          url: `${siteUrl}/locations`,
        },
      },
    ],
  },
];

const locations = [
  {
    id: 'retford',
    name: 'Electrician Retford',
    area: 'Retford, Nottinghamshire',
    postcode: 'DN22',
    distance: '8 miles from Worksop',
    image: 'https://readdy.ai/api/search-image?query=Retford%20town%20high%20street%20Nottinghamshire%20UK%2C%20traditional%20English%20market%20town%2C%20brick%20buildings%2C%20town%20centre%2C%20daytime%2C%20realistic%20photography%2C%20warm%20autumn%20light&width=900&height=600&seq=location-retford-nottinghamshire&orientation=landscape',
    description: 'Looking for a reliable electrician in Retford? We provide the full range of electrical services to homeowners, landlords, and small businesses across Retford and the surrounding villages. Based in nearby Worksop, we offer fast response times and the same trusted service to all our Retford customers.',
    services: [
      'Emergency Electrician Retford',
      'House Rewiring Retford',
      'Fuse Board Upgrade Retford',
      'EICR Certificate Retford',
      'Domestic Electrician Retford',
      'Landlord Electrical Services',
    ],
    areas: ['East Retford', 'Ordsall', 'Gamston', 'Treswell', 'North Leverton', 'Babworth'],
    faq: [
      {
        q: 'Do you cover all of Retford?',
        a: 'Yes, we cover all of Retford and the surrounding villages in the DN22 postcode area. We typically arrive within 60 minutes for standard jobs and 90 minutes for emergencies.',
      },
      {
        q: 'Can you carry out EICR tests for Retford landlords?',
        a: 'Absolutely. We carry out EICR inspections for Retford landlords with fast turnaround. We\'ll issue your certificate the same day in most cases.',
      },
    ],
  },
  {
    id: 'carlton',
    name: 'Electrician Carlton in Lindrick',
    area: 'Carlton in Lindrick, South Yorkshire',
    postcode: 'S81',
    distance: '3 miles from Worksop',
    image: 'https://readdy.ai/api/search-image?query=Carlton%20in%20Lindrick%20village%20South%20Yorkshire%20UK%2C%20traditional%20English%20village%20residential%20area%2C%20brick%20houses%2C%20quiet%20village%20street%2C%20green%20trees%2C%20daytime%2C%20warm%20natural%20lighting%2C%20realistic%20photography&width=900&height=600&seq=location-carlton-lindrick-worksop&orientation=landscape',
    description: 'Need an electrician in Carlton in Lindrick? We\'re just minutes away and cover the whole of Carlton in Lindrick, including the new housing developments. Whether it\'s an emergency callout, EICR test, or a full rewire, we\'re your local electrician on your doorstep.',
    services: [
      'Emergency Electrician Carlton',
      'House Rewiring Carlton in Lindrick',
      'Fuse Board Upgrade',
      'EICR Certificate Carlton',
      'New Build Snagging',
      'EV Charger Installation',
    ],
    areas: ['Carlton in Lindrick', 'Wallingwells', 'Costhorpe', 'Langold', 'Wales', 'North Anston'],
    faq: [
      {
        q: 'How quickly can you reach Carlton in Lindrick?',
        a: 'Carlton in Lindrick is just 3 miles from our Worksop base. For emergencies, we aim to be with you within 30 minutes. For standard jobs, we\'ll agree a convenient appointment time.',
      },
      {
        q: 'Do you work on new builds in Carlton?',
        a: 'Yes, we work on new builds as well as older properties across Carlton in Lindrick. This includes snagging electrical issues, adding extra sockets, and installing EV chargers.',
      },
    ],
  },
  {
    id: 'shireoaks',
    name: 'Electrician Shireoaks',
    area: 'Shireoaks, Nottinghamshire',
    postcode: 'S81',
    distance: '2 miles from Worksop',
    image: 'https://readdy.ai/api/search-image?query=Shireoaks%20village%20Nottinghamshire%20UK%2C%20traditional%20English%20residential%20village%2C%20brick%20terraced%20houses%2C%20quiet%20residential%20street%2C%20mature%20trees%2C%20daytime%2C%20warm%20natural%20light%2C%20realistic%20photography&width=900&height=600&seq=location-shireoaks-worksop&orientation=landscape',
    description: 'Your local electrician for Shireoaks. Just 2 miles from Worksop, we offer the fastest response times to Shireoaks residents. All our work is certified, Part P compliant, and backed by our satisfaction guarantee. We\'re the electrician Shireoaks families call first.',
    services: [
      'Emergency Electrician Shireoaks',
      'House Rewiring Shireoaks',
      'Fuse Board Upgrade Shireoaks',
      'EICR Certificate Shireoaks',
      'Domestic Electrics',
      'Outdoor & Garden Lighting',
    ],
    areas: ['Shireoaks', 'Rhodesia', 'Gateford', 'Turnerwood', 'Woodsetts', 'Letwell'],
    faq: [
      {
        q: 'Are you the closest electrician to Shireoaks?',
        a: 'We\'re based in Worksop, just 2 miles away — making us one of the closest professional electricians to Shireoaks. Perfect for fast emergency response.',
      },
      {
        q: 'Can you install garden and outdoor lighting in Shireoaks?',
        a: 'Yes, outdoor and garden lighting is one of our most popular services in Shireoaks. We install security lighting, garden feature lighting, and outdoor sockets.',
      },
    ],
  },
];

export default function LocationsPage() {
  const [activeLocation, setActiveLocation] = useState(locations[0].id);
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const current = locations.find((l) => l.id === activeLocation) ?? locations[0];

  return (
    <div className="min-h-screen bg-white font-body">
      <SEOHead
        title="Electrician Retford, Carlton in Lindrick & Shireoaks | Areas Covered"
        description="Reliable electrician covering Retford, Carlton in Lindrick, and Shireoaks. Fast response from our Worksop base — emergency callouts, rewiring, EICR and more. NAPIT registered."
        keywords="electrician Retford, electrician Carlton in Lindrick, electrician Shireoaks, local electrician Worksop, emergency electrician Nottinghamshire"
        canonical={`${siteUrl}/locations`}
        ogTitle="Electrician Retford, Carlton in Lindrick &amp; Shireoaks | Areas Covered"
        ogDescription="Local electrical services covering Retford, Carlton in Lindrick and Shireoaks. Fast response, honest pricing, fully insured."
        ogUrl={`${siteUrl}/locations`}
        ogImage={LOCATIONS_OG_IMAGE}
        jsonLd={locationsJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-navy-800 pt-32 pb-16">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20Worksop%20town%20Nottinghamshire%20England%20UK%2C%20residential%20area%20suburban%20houses%20streets%2C%20green%20fields%20in%20background%2C%20daytime%2C%20realistic%20photography&width=1600&height=500&seq=locations-hero-worksop-aerial&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/70 to-navy-800"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-amber-400 cursor-pointer">Home</Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-gray-500"></i>
            </span>
            <span className="text-amber-400">Locations</span>
          </nav>
          <h1 className="font-heading font-extrabold text-white text-5xl mb-4">
            Areas We <span className="text-amber-400">Cover</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
            Based in Worksop, we provide professional electrical services across North Nottinghamshire and South Yorkshire — including Retford, Carlton in Lindrick, and Shireoaks.
          </p>
        </div>
      </section>

      {/* Location Tabs */}
      <section className="border-b border-gray-200 bg-white sticky top-8 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 py-3 overflow-x-auto">
            {locations.map((loc) => (
              <button
                key={loc.id}
                onClick={() => { setActiveLocation(loc.id); setOpenFaq(null); }}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-md font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                  activeLocation === loc.id
                    ? 'bg-amber-500 text-navy-800'
                    : 'text-gray-500 hover:text-navy-800 hover:bg-gray-100'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-fill text-sm"></i>
                </span>
                {loc.area.split(',')[0]}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Location Detail */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Main */}
            <div className="lg:col-span-2">
              {/* Image */}
              <div className="rounded-xl overflow-hidden mb-8" style={{ height: '360px' }}>
                <img
                  src={current.image}
                  alt={current.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Badge */}
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-map-pin-fill text-xs"></i>
                  </span>
                  {current.area}
                </span>
                <span className="text-gray-400 text-sm">{current.postcode} · {current.distance}</span>
              </div>

              <h2 className="font-heading font-extrabold text-navy-800 text-4xl mb-3">{current.name}</h2>
              <p className="text-gray-600 leading-relaxed mb-8 text-base">{current.description}</p>

              {/* Services in this area */}
              <h3 className="font-heading font-bold text-navy-800 text-xl mb-4">Services Available in {current.area.split(',')[0]}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
                {current.services.map((s) => (
                  <div key={s} className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-flashlight-fill text-amber-500"></i>
                    </span>
                    <span className="text-navy-800 font-semibold text-sm">{s}</span>
                  </div>
                ))}
              </div>

              {/* Villages covered */}
              <h3 className="font-heading font-bold text-navy-800 text-xl mb-4">Villages &amp; Areas Covered</h3>
              <div className="flex flex-wrap gap-2 mb-10">
                {current.areas.map((a) => (
                  <span key={a} className="inline-flex items-center gap-1.5 bg-navy-800 text-white text-xs font-semibold px-3 py-2 rounded-full">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-map-pin-2-fill text-amber-400 text-xs"></i>
                    </span>
                    {a}
                  </span>
                ))}
              </div>

              {/* FAQ */}
              <h3 className="font-heading font-bold text-navy-800 text-xl mb-5">
                Frequently Asked Questions — {current.area.split(',')[0]}
              </h3>
              <div className="space-y-3">
                {current.faq.map((item) => (
                  <div key={item.q} className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === item.q ? null : item.q)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-navy-800 text-sm">{item.q}</span>
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-4">
                        <i className={`${openFaq === item.q ? 'ri-subtract-line' : 'ri-add-line'} text-amber-500`}></i>
                      </span>
                    </button>
                    {openFaq === item.q && (
                      <div className="px-5 pb-4 text-gray-600 text-sm leading-relaxed border-t border-gray-100">
                        {item.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 space-y-5">
                {/* Phone */}
                <div className="bg-navy-800 rounded-xl p-6 text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-amber-500 rounded-full mx-auto mb-4">
                    <i className="ri-phone-fill text-navy-800 text-2xl"></i>
                  </div>
                  <h3 className="font-heading font-bold text-white text-lg mb-1">Call Your Local Electrician</h3>
                  <p className="text-gray-400 text-sm mb-4">Call us for a free quote</p>
                  <a
                    href={`tel:${PHONE.replace(/\s/g, '')}`}
                    className="block bg-amber-500 hover:bg-amber-400 text-navy-800 font-bold text-lg py-3.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {PHONE}
                  </a>
                  <p className="text-gray-500 text-xs mt-3">No call-out charge for quotes</p>
                </div>

                {/* Why choose */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <h3 className="font-heading font-bold text-navy-800 text-lg mb-4">Why Choose Us in {current.area.split(',')[0]}?</h3>
                  <div className="space-y-3">
                    {[
                      { icon: 'ri-map-pin-fill', text: `Local to ${current.area.split(',')[0]} — fast response` },
                      { icon: 'ri-shield-check-fill', text: 'Fully insured & NAPIT registered' },
                      { icon: 'ri-money-pound-circle-fill', text: 'Honest, transparent pricing' },
                      { icon: 'ri-star-fill', text: '5-star Google rated' },
                      { icon: 'ri-calendar-check-fill', text: 'Same-day quotes available' },
                    ].map((item) => (
                      <div key={item.text} className="flex items-start gap-3">
                        <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className={`${item.icon} text-amber-500 text-sm`}></i>
                        </span>
                        <span className="text-navy-800 text-sm font-medium">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* All locations */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <h3 className="font-heading font-bold text-navy-800 text-base mb-4">All Locations We Cover</h3>
                  <div className="space-y-2">
                    {locations.map((loc) => (
                      <button
                        key={loc.id}
                        onClick={() => setActiveLocation(loc.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer text-left ${
                          activeLocation === loc.id
                            ? 'bg-amber-500 text-navy-800 font-bold'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-map-pin-fill text-xs"></i>
                        </span>
                        {loc.area.split(',')[0]}
                      </button>
                    ))}
                    <Link
                      to="/"
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-all cursor-pointer"
                    >
                      <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-home-4-fill text-xs"></i>
                      </span>
                      Worksop (Home Base)
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="font-heading font-extrabold text-navy-800 text-3xl mb-2">Our Coverage Area</h2>
            <p className="text-gray-500">Covering Worksop, Retford, Carlton in Lindrick, Shireoaks and all surrounding areas.</p>
          </div>
          <div className="rounded-xl overflow-hidden border border-gray-200" style={{ height: '400px' }}>
            <iframe
              title="Electrician service coverage area map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d76527.71!2d-1.15!3d53.30!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4879b73e1e6b86a9%3A0x5c15ad3b5df5e4d6!2sWorksop!5e0!3m2!1sen!2suk!4v1710000000000!5m2!1sen!2suk"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)' }} className="py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-extrabold text-white text-4xl mb-3">
            Need an Electrician in {current.area.split(',')[0]}?
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Fast response, honest pricing, quality workmanship. Call now or request a free quote.
          </p>
          <a
            href={`tel:${PHONE.replace(/\s/g, '')}`}
            className="inline-flex items-center gap-3 bg-navy-800 hover:bg-navy-700 text-white font-bold text-lg px-10 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-6 h-6 flex items-center justify-center">
              <i className="ri-phone-fill text-amber-400 text-xl"></i>
            </span>
            Call {PHONE}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
